import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Check, Clock, Link as LinkIcon, Loader2, Plus, Trash2, X, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';

type Appointment = {
  id: string;
  title: string | null;
  start_at: string;
  end_at: string;
  status: string;
  contact_id: string | null;
  notes: string | null;
  location: string | null;
  meeting_url: string | null;
};

type Slot = {
  id: string;
  start_at: string;
  end_at: string;
  status: 'available' | 'held' | 'booked' | 'cancelled';
  notes: string | null;
  appointment_id: string | null;
  calendar_account_id: string | null;
};

type Contact = { id: string; full_name: string | null; phone: string | null; email: string | null; company: string | null };

type CalendarAccount = {
  id: string;
  provider: string;
  display_name: string | null;
  status: string;
  config: Record<string, any> | null;
  connected_at: string | null;
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatTimeRange = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const sameDay = startDate.toDateString() === endDate.toDateString();
  const dayLabel = startDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = startDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const endTime = endDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return sameDay ? `${dayLabel} · ${startTime} - ${endTime}` : `${formatDateTime(start)} → ${formatDateTime(end)}`;
};

const badgeStyles: Record<string, string> = {
  available: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700',
  held: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700',
  booked: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-700',
  cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700',
  canceled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700',
  scheduled: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-700',
  completed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700',
  cancelled_appt: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700',
};

const formatStatusLabel = (status: string) => (status === 'canceled' ? 'cancelled' : status);

export const AppointmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { orgId, loading: orgLoading } = useOrgContext();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [calendarAccounts, setCalendarAccounts] = useState<CalendarAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotForm, setSlotForm] = useState({ start: '', duration: 30, notes: '', calendarAccountId: '' });
  const [accountForm, setAccountForm] = useState({ id: '', displayName: '', provider: 'manual', icsUrl: '', bookingLink: '' });
  const [bookingModal, setBookingModal] = useState({
    open: false,
    slot: null as Slot | null,
    contactId: '',
    newContactName: '',
    newContactPhone: '',
    newContactEmail: '',
    title: '',
    notes: '',
    type: 'call' as 'call' | 'teams' | 'person',
  });
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);

  const syncCalendars = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return;
      await fetch('/api/calendar/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn('Calendar sync failed', err);
    }
  }, []);

  const notifyAppointment = useCallback(async (payload: any) => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return;
      await fetch('/api/calendar/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('Calendar notification failed', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      await syncCalendars();
      const now = new Date();
      now.setUTCDate(now.getUTCDate() - 1);
      const [slotsResp, apptsResp, contactsResp, accountsResp] = await Promise.all([
        supabase
          .from('appointment_slots')
          .select('id, start_at, end_at, status, notes, appointment_id, calendar_account_id')
          .eq('org_id', orgId)
          .gte('start_at', now.toISOString())
          .order('start_at', { ascending: true }),
        supabase
          .from('appointments')
          .select('id, title, start_at, end_at, status, contact_id, notes, location, meeting_url')
          .eq('org_id', orgId)
          .gte('start_at', now.toISOString())
          .order('start_at', { ascending: true }),
        supabase
          .from('contacts')
          .select('id, full_name, phone, email, company')
          .eq('org_id', orgId)
          .order('full_name', { ascending: true })
          .limit(200),
        supabase
          .from('calendar_accounts')
          .select('id, provider, display_name, status, config, connected_at')
          .eq('org_id', orgId)
          .order('created_at', { ascending: true }),
      ]);

      if (slotsResp.error) throw slotsResp.error;
      if (apptsResp.error) throw apptsResp.error;
      if (contactsResp.error) throw contactsResp.error;
      if (accountsResp.error) throw accountsResp.error;

      setSlots((slotsResp.data as Slot[]) ?? []);
      setAppointments((apptsResp.data as Appointment[]) ?? []);
      setContacts((contactsResp.data as Contact[]) ?? []);
      const accounts = (accountsResp.data as CalendarAccount[]) ?? [];
      setCalendarAccounts(accounts);
      if (!slotForm.calendarAccountId && accounts.length > 0) {
        setSlotForm((prev) => ({ ...prev, calendarAccountId: accounts[0].id }));
      }
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.loadData'));
    } finally {
      setLoading(false);
    }
  }, [orgId, slotForm.calendarAccountId, syncCalendars]);

  useEffect(() => {
    if (!orgId) {
      if (!orgLoading) {
        setSlots([]);
        setAppointments([]);
        setContacts([]);
        setCalendarAccounts([]);
        setLoading(false);
      }
      return;
    }
    fetchData();
  }, [orgId, orgLoading, fetchData]);

  const contactIndex = useMemo(() => {
    const map: Record<string, Contact> = {};
    contacts.forEach((c) => {
      if (c.id) map[c.id] = c;
    });
    return map;
  }, [contacts]);

  const createSlot = async (event: FormEvent) => {
    event.preventDefault();
    if (!orgId || !slotForm.start) return;
    try {
      const startIso = new Date(slotForm.start).toISOString();
      const endIso = new Date(new Date(slotForm.start).getTime() + slotForm.duration * 60000).toISOString();
      const payload: any = {
        org_id: orgId,
        start_at: startIso,
        end_at: endIso,
        notes: slotForm.notes || null,
        status: 'available',
      };
      if (slotForm.calendarAccountId) payload.calendar_account_id = slotForm.calendarAccountId;
      const { error } = await supabase.from('appointment_slots').insert([payload]);
      if (error) throw error;
      setSlotForm((prev) => ({ ...prev, start: '', notes: '' }));
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.createSlot'));
    }
  };

  const cancelSlot = async (slotId: string) => {
    if (!orgId) return;
    try {
      const { error } = await supabase
        .from('appointment_slots')
        .update({ status: 'cancelled' })
        .eq('id', slotId)
        .eq('org_id', orgId);
      if (error) throw error;
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.cancelSlot'));
    }
  };

  const openBooking = (slot: Slot) => {
      setBookingModal({
      open: true,
      slot,
      contactId: '',
      newContactName: '',
      newContactPhone: '',
      newContactEmail: '',
      title: '',
      notes: '',
      type: 'call',
    });
    setContactSearchQuery('');
    setIsContactDropdownOpen(false);
  };

  const bookSlot = async (event: FormEvent) => {
    event.preventDefault();
    if (!orgId || !bookingModal.slot) return;
    try {
      let contactId = bookingModal.contactId;
      if (!contactId) {
        if (!bookingModal.newContactName && !bookingModal.newContactPhone) {
          setError(t('crm.appointments.errors.selectContact'));
          return;
        }
        const { data: newContact, error: contactError } = await supabase
          .from('contacts')
          .insert([
            {
              org_id: orgId,
              full_name: bookingModal.newContactName || null,
              phone: bookingModal.newContactPhone || null,
              email: bookingModal.newContactEmail || null,
              source: 'calendar',
            },
          ])
          .select('id')
          .single();
        if (contactError) throw contactError;
        contactId = newContact?.id ?? '';
      }
      if (!contactId) throw new Error(t('crm.appointments.errors.contactCreationFailed'));

      let location = 'Telefonata';
      if (bookingModal.type === 'teams') location = 'Microsoft Teams';
      else if (bookingModal.type === 'person') location = 'Di Persona';

      const appointmentPayload = {
        org_id: orgId,
        contact_id: contactId,
        start_at: bookingModal.slot.start_at,
        end_at: bookingModal.slot.end_at,
        status: 'scheduled',
        title: bookingModal.title,
        notes: bookingModal.notes || null,
        slot_id: bookingModal.slot.id,
        calendar_account_id: bookingModal.slot.calendar_account_id || null,
        source: 'calendar',
        location: location,
      };

      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .insert([appointmentPayload])
        .select('id')
        .single();
      if (appointmentError) throw appointmentError;

      const contactDetails = contactIndex[contactId] || {
        full_name: bookingModal.newContactName,
        email: bookingModal.newContactEmail,
        phone: bookingModal.newContactPhone,
      };

      const notifyPayload = {
        appointment: {
          id: appointment?.id,
          title: appointmentPayload.title,
          start_at: bookingModal.slot.start_at,
          end_at: bookingModal.slot.end_at,
          location: bookingModal.slot?.notes || bookingModal.notes || null,
          notes: bookingModal.notes || null,
        },
        contact: {
          id: contactId,
          name: contactDetails?.full_name || bookingModal.newContactName,
          email: contactDetails?.email || bookingModal.newContactEmail,
          phone: contactDetails?.phone || bookingModal.newContactPhone,
        },
      };

      const { error: slotError } = await supabase
        .from('appointment_slots')
        .update({ status: 'booked', appointment_id: appointment?.id || null })
        .eq('id', bookingModal.slot.id)
        .eq('org_id', orgId);
      if (slotError) throw slotError;

        setBookingModal({
          open: false,
          slot: null,
          contactId: '',
          newContactName: '',
          newContactPhone: '',
          newContactEmail: '',
          title: '',
          notes: '',
          type: 'call',
        });
      await notifyAppointment(notifyPayload);
      await fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.bookAppointment'));
    }
  };

  const markAppointment = async (appointmentId: string, status: string) => {
    if (!orgId) return;
    try {
      const normalizedStatus = status === 'cancelled' ? 'canceled' : status;
      const values: any = { status: normalizedStatus };
      if (normalizedStatus === 'canceled') {
        values.reminder_status = 'skipped';
      }
      const { error } = await supabase
        .from('appointments')
        .update(values)
        .eq('id', appointmentId)
        .eq('org_id', orgId);
      if (error) throw error;
      if (normalizedStatus === 'canceled') {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (token) {
          await fetch('/api/calendar/cancel', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ appointment_id: appointmentId }),
          });
        }
      }
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.updateAppointment'));
    }
  };

  const deleteAppointment = async (appointmentId: string) => {
    if (!orgId) return;
    if (!confirm(t('crm.appointments.delete.confirm'))) return;
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appointmentId)
        .eq('org_id', orgId);
      if (error) throw error;
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.deleteAppointment'));
    }
  };

  const deleteBookedSlot = async (slotId: string) => {
    if (!orgId) return;
    if (!confirm(t('crm.appointments.deleteSlot.confirm'))) return;
    try {
      // First, check if there's an associated appointment
      const slot = slots.find(s => s.id === slotId);
      if (slot?.appointment_id) {
        // Delete the associated appointment first
        const { error: appointmentError } = await supabase
          .from('appointments')
          .delete()
          .eq('id', slot.appointment_id)
          .eq('org_id', orgId);
        if (appointmentError) throw appointmentError;
      }
      // Then delete the slot
      const { error } = await supabase
        .from('appointment_slots')
        .delete()
        .eq('id', slotId)
        .eq('org_id', orgId);
      if (error) throw error;
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.deleteSlot'));
    }
  };

  const saveAccount = async (event: FormEvent) => {
    event.preventDefault();
    if (!orgId) return;
    try {
      const payload: any = {
        org_id: orgId,
        provider: accountForm.provider,
        display_name: accountForm.displayName || t('crm.appointments.calendarSync.providerManual'),
        status: (accountForm.icsUrl || accountForm.bookingLink) ? 'connected' : 'disconnected',
        config: {
          ...(accountForm.icsUrl ? { ics_url: accountForm.icsUrl } : {}),
          ...(accountForm.bookingLink ? { booking_link: accountForm.bookingLink } : {}),
        },
      };
      if (accountForm.id) {
        const { error } = await supabase
          .from('calendar_accounts')
          .update(payload)
          .eq('id', accountForm.id)
          .eq('org_id', orgId);
        if (error) throw error;
      } else {
        const existingManual = calendarAccounts.find((acc) => acc.provider === accountForm.provider);
        if (existingManual) {
          const { error } = await supabase
            .from('calendar_accounts')
            .update(payload)
            .eq('id', existingManual.id)
            .eq('org_id', orgId);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('calendar_accounts').insert([payload]);
          if (error) throw error;
        }
      }
      setAccountForm({ id: '', displayName: '', provider: 'manual', icsUrl: '', bookingLink: '' });
      await syncCalendars();
      fetchData();
    } catch (err: any) {
      setError(err.message || t('crm.appointments.errors.saveConnection'));
    }
  };

  const availableSlots = useMemo(() => slots.filter((slot) => slot.status === 'available'), [slots]);
  const bookedSlots = useMemo(() => slots.filter((slot) => slot.status === 'booked'), [slots]);
  const upcomingAppointments = useMemo(() => {
    const activeStatuses = new Set(['scheduled', 'confirmed']);
    return appointments
      .filter((appt) => activeStatuses.has((appt.status || '').toLowerCase()))
      .slice(0, 20);
  }, [appointments]);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{t('crm.appointments.subtitle')}</h1>
      {error && (
        <div className="mb-4 rounded border border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400 shadow-md">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('crm.appointments.loading')}
        </div>
      ) : (
        <div className="space-y-8">
          <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.calendarSync.title')}</div>
                <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{t('crm.appointments.calendarSync.heading')}</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {t('crm.appointments.calendarSync.description')}
                </p>
              </div>
            </div>
            {calendarAccounts.length > 0 && (
              <div className="mt-4 grid gap-3">
                {calendarAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-300"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{account.display_name || account.provider}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('crm.appointments.calendarSync.status')}: {account.status}</span>
                      {account.config?.ics_url && (
                        <span className="mt-1 inline-flex items-start gap-1 text-xs text-cyan-600 dark:text-cyan-400 break-all">
                          <LinkIcon className="h-3 w-3 flex-shrink-0 mt-0.5" /> <span className="break-words">{account.config.ics_url}</span>
                        </span>
                      )}
                      {account.config?.booking_link && (
                        <span className="mt-1 inline-flex items-start gap-1 text-xs text-emerald-600 dark:text-emerald-400 break-all">
                          <LinkIcon className="h-3 w-3 flex-shrink-0 mt-0.5" /> <span className="break-words">{account.config.booking_link}</span>
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setAccountForm({
                          id: account.id,
                          provider: account.provider,
                          displayName: account.display_name || '',
                          icsUrl: account.config?.ics_url || '',
                          bookingLink: account.config?.booking_link || '',
                        })
                      }
                      className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0 self-start sm:self-auto"
                    >
                      {t('crm.appointments.calendarSync.edit')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={saveAccount} className="mt-4 grid gap-3 md:grid-cols-4">
              <div className="md:col-span-1">
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.calendarSync.displayName')}</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={accountForm.displayName}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  placeholder={t('crm.appointments.calendarSync.displayNamePlaceholder')}
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.calendarSync.provider')}</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                  value={accountForm.provider}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, provider: e.target.value }))}
                >
                  <option value="manual">{t('crm.appointments.calendarSync.providerManual')}</option>
                  <option value="microsoft">{t('crm.appointments.calendarSync.providerMicrosoft')}</option>
                  <option value="calendly">{t('crm.appointments.calendarSync.providerCalendly')}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.calendarSync.icsLink')}</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={accountForm.icsUrl}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, icsUrl: e.target.value }))}
                  placeholder={t('crm.appointments.calendarSync.icsLinkPlaceholder')}
                />
              </div>
              <div className="md:col-span-4">
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">Booking Link</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  value={accountForm.bookingLink}
                  onChange={(e) => setAccountForm((prev) => ({ ...prev, bookingLink: e.target.value }))}
                  placeholder="https://outlook.office365.com/..."
                />
              </div>
              <div className="md:col-span-4 flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600 transition-colors shadow-md"
                >
                  <Plus className="h-4 w-4" /> {t('crm.appointments.calendarSync.saveConnection')}
                </button>
              </div>
            </form>
          </section>

          <section className="grid gap-6 lg:grid-cols-[320px_1fr]">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-md">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <CalendarClock className="h-4 w-4 text-cyan-600 dark:text-cyan-400" /> {t('crm.appointments.buildAvailability.title')}
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t('crm.appointments.buildAvailability.description')}
              </p>
              <form onSubmit={createSlot} className="mt-4 grid gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.buildAvailability.startDateTime')}</label>
                  <input
                    type="datetime-local"
                    required
                    className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                    value={slotForm.start}
                    onChange={(e) => setSlotForm((prev) => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.buildAvailability.duration')}</label>
                  <input
                    type="number"
                    min={15}
                    step={15}
                    className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                    value={slotForm.duration}
                    onChange={(e) => setSlotForm((prev) => ({ ...prev, duration: Number(e.target.value) }))}
                  />
                </div>
                {calendarAccounts.length > 0 && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.buildAvailability.calendar')}</label>
                    <select
                      className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                      value={slotForm.calendarAccountId}
                      onChange={(e) => setSlotForm((prev) => ({ ...prev, calendarAccountId: e.target.value }))}
                    >
                      {calendarAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.display_name || account.provider}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.buildAvailability.internalNotes')}</label>
                  <textarea
                    className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={slotForm.notes}
                    onChange={(e) => setSlotForm((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder={t('crm.appointments.buildAvailability.internalNotesPlaceholder')}
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 transition-colors shadow-md"
                >
                  <Plus className="h-4 w-4" /> {t('crm.appointments.buildAvailability.addSlot')}
                </button>
              </form>
            </div>

            <div className="grid gap-6">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.availableSlots.title')}</div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('crm.appointments.availableSlots.subtitle')}</h3>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('crm.appointments.availableSlots.open', { count: availableSlots.length })}</span>
                </div>
                {availableSlots.length === 0 ? (
                  <div className="rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-4 text-sm text-gray-500 dark:text-gray-400">
                    {t('crm.appointments.availableSlots.empty')}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableSlots.map((slot) => (
                      <div key={slot.id} className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-3 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{formatTimeRange(slot.start_at, slot.end_at)}</div>
                            {slot.notes && <div className="text-xs text-gray-500 dark:text-gray-400">{slot.notes}</div>}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openBooking(slot)}
                              className="rounded border border-sky-500 dark:border-sky-400 px-3 py-1.5 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 transition-colors"
                            >
                              {t('crm.appointments.availableSlots.book')}
                            </button>
                            <button
                              onClick={() => cancelSlot(slot.id)}
                              className="rounded border border-red-500 dark:border-red-400 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                            >
                              {t('crm.appointments.availableSlots.cancel')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {bookedSlots.length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
                  <div className="mb-3 text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.bookedSlots.title')}</div>
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    {bookedSlots.map((slot) => (
                      <div key={slot.id} className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{formatTimeRange(slot.start_at, slot.end_at)}</div>
                            {slot.notes && <div className="text-xs text-gray-500 dark:text-gray-400">{slot.notes}</div>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-3 py-1 text-xs ${badgeStyles[slot.status] || ''}`}>{slot.status}</span>
                            <button
                              onClick={() => deleteBookedSlot(slot.id)}
                              className="rounded border border-red-600 dark:border-red-500 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                              title={t('crm.appointments.deleteSlot.title')}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.upcoming.title')}</div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('crm.appointments.upcoming.subtitle')}</h2>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('crm.appointments.upcoming.scheduled', { count: upcomingAppointments.length })}</div>
            </div>
            {upcomingAppointments.length === 0 ? (
              <div className="rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-4 text-sm text-gray-500 dark:text-gray-400">
                {t('crm.appointments.upcoming.empty')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-[0.3em] text-gray-700 dark:text-gray-300">
                    <tr>
                      <th className="px-4 py-2 font-semibold">{t('crm.appointments.upcoming.when')}</th>
                      <th className="px-4 py-2 font-semibold">{t('crm.appointments.upcoming.contact')}</th>
                      <th className="px-4 py-2 font-semibold">{t('crm.appointments.upcoming.subject')}</th>
                      <th className="px-4 py-2 font-semibold">{t('crm.appointments.upcoming.status')}</th>
                      <th className="px-4 py-2 font-semibold">{t('crm.appointments.upcoming.actions')}</th>
              </tr>
            </thead>
            <tbody>
                    {upcomingAppointments.map((appt) => {
                      const contact = appt.contact_id ? contactIndex[appt.contact_id] : undefined;
                      const statusLabel = (appt.status || '').toLowerCase();
                      const isActionable = statusLabel === 'scheduled' || statusLabel === 'confirmed';
                      return (
                        <tr key={appt.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatTimeRange(appt.start_at, appt.end_at)}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{contact?.full_name || '—'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{contact?.phone || contact?.email || '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{appt.title || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs ${badgeStyles[appt.status] || ''}`}>{formatStatusLabel(appt.status)}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            <div className="flex gap-2">
                              {appt.meeting_url && isActionable && (
                                <a
                                  href={appt.meeting_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 rounded border border-sky-500 dark:border-sky-400 px-3 py-1 text-xs text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 transition-colors"
                                >
                                  <LinkIcon className="h-3 w-3" /> {t('crm.appointments.upcoming.join')}
                                </a>
                              )}
                              <button
                                onClick={() => markAppointment(appt.id, 'completed')}
                                disabled={!isActionable}
                                className={`inline-flex items-center gap-1 rounded border border-emerald-500 dark:border-emerald-400 px-3 py-1 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors ${
                                  !isActionable ? 'cursor-not-allowed opacity-40 hover:bg-transparent dark:hover:bg-transparent' : ''
                                }`}
                              >
                                <Check className="h-3 w-3" /> {t('crm.appointments.upcoming.done')}
                              </button>
                              <button
                                onClick={() => markAppointment(appt.id, 'cancelled')}
                                disabled={!isActionable}
                                className={`inline-flex items-center gap-1 rounded border border-red-500 dark:border-red-400 px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors ${
                                  !isActionable ? 'cursor-not-allowed opacity-40 hover:bg-transparent dark:hover:bg-transparent' : ''
                                }`}
                              >
                                <X className="h-3 w-3" /> {t('crm.appointments.upcoming.cancel')}
                              </button>
                              <button
                                onClick={() => deleteAppointment(appt.id)}
                                className="inline-flex items-center gap-1 rounded border border-red-600 dark:border-red-500 px-3 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                                title={t('crm.appointments.delete.title')}
                              >
                                <Trash2 className="h-3 w-3" /> {t('crm.appointments.delete.button')}
                              </button>
                            </div>
                          </td>
                </tr>
                      );
                    })}
            </tbody>
          </table>
              </div>
            )}
          </section>
        </div>
      )}

      {bookingModal.open && bookingModal.slot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 md:p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.bookTitle')}</div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatTimeRange(bookingModal.slot.start_at, bookingModal.slot.end_at)}</h3>
              </div>
              <button
                onClick={() => setBookingModal({
                  open: false,
                  slot: null,
                  contactId: '',
                  newContactName: '',
                  newContactPhone: '',
                  newContactEmail: '',
                  title: '',
                  notes: '',
                  type: 'call',
                })}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={bookSlot} className="mt-4 grid gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.existingContact')}</label>
                <div className="mt-1 relative">
                  <button
                    type="button"
                    onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
                    className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 flex items-center justify-between"
                  >
                    <span className="truncate block">
                      {bookingModal.contactId 
                        ? (() => {
                            const c = contactIndex[bookingModal.contactId];
                            if (!c) return t('crm.appointments.modal.selectContact');
                            return c.company 
                              ? `${c.company}${c.full_name ? ` - ${c.full_name}` : ''}`
                              : (c.full_name || c.email || c.phone || c.id);
                          })()
                        : t('crm.appointments.modal.selectContact')
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>

                  {isContactDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setIsContactDropdownOpen(false)}
                      />
                      <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 pl-8 pr-2 py-1.5 text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                              placeholder="Cerca contatto..."
                              value={contactSearchQuery}
                              onChange={(e) => setContactSearchQuery(e.target.value)}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-1">
                          <button
                            type="button"
                            onClick={() => {
                              setBookingModal(prev => ({ ...prev, contactId: '' }));
                              setIsContactDropdownOpen(false);
                            }}
                            className="w-full text-left px-2 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md mb-1"
                          >
                            {t('crm.appointments.modal.selectContact')} (Nessuno)
                          </button>
                          {contacts
                            .filter(c => {
                              if (!contactSearchQuery) return true;
                              const q = contactSearchQuery.toLowerCase();
                              return (
                                (c.full_name || '').toLowerCase().includes(q) ||
                                (c.email || '').toLowerCase().includes(q) ||
                                (c.phone || '').toLowerCase().includes(q) ||
                                (c.company || '').toLowerCase().includes(q)
                              );
                            })
                            .map(contact => {
                              const label = contact.company 
                                ? `${contact.company}${contact.full_name ? ` - ${contact.full_name}` : ''}`
                                : (contact.full_name || contact.email || contact.phone || contact.id);
                              
                              return (
                                <button
                                  key={contact.id}
                                  type="button"
                                  onClick={() => {
                                    setBookingModal(prev => ({ ...prev, contactId: contact.id }));
                                    setIsContactDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md ${
                                    bookingModal.contactId === contact.id 
                                      ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300' 
                                      : 'text-gray-900 dark:text-gray-100'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                            {contacts.filter(c => {
                              if (!contactSearchQuery) return true;
                              const q = contactSearchQuery.toLowerCase();
                              return (
                                (c.full_name || '').toLowerCase().includes(q) ||
                                (c.email || '').toLowerCase().includes(q) ||
                                (c.phone || '').toLowerCase().includes(q) ||
                                (c.company || '').toLowerCase().includes(q)
                              );
                            }).length === 0 && (
                              <div className="px-2 py-3 text-xs text-center text-gray-500">
                                Nessun risultato trovato
                              </div>
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.orCreateNew')}</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder={t('crm.appointments.modal.fullName')}
                    value={bookingModal.newContactName}
                    onChange={(e) => setBookingModal((prev) => ({ ...prev, newContactName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.phone')}</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder={t('crm.appointments.modal.phonePlaceholder')}
                    value={bookingModal.newContactPhone}
                    onChange={(e) => setBookingModal((prev) => ({ ...prev, newContactPhone: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.email')}</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder={t('crm.appointments.modal.emailPlaceholder')}
                  value={bookingModal.newContactEmail}
                  onChange={(e) => setBookingModal((prev) => ({ ...prev, newContactEmail: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">Tipo Appuntamento</label>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingModal(prev => ({ ...prev, type: 'call' }))}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      bookingModal.type === 'call'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:border-cyan-500 dark:bg-cyan-900/30 dark:text-cyan-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    Telefonata
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingModal(prev => ({ ...prev, type: 'teams' }))}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      bookingModal.type === 'teams'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:border-cyan-500 dark:bg-cyan-900/30 dark:text-cyan-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    Teams
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingModal(prev => ({ ...prev, type: 'person' }))}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      bookingModal.type === 'person'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:border-cyan-500 dark:bg-cyan-900/30 dark:text-cyan-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    Di Persona
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.title')} *</label>
                <input
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder={t('crm.appointments.modal.titlePlaceholder')}
                  value={bookingModal.title}
                  onChange={(e) => setBookingModal((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.appointments.modal.notes')}</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder={t('crm.appointments.modal.notesPlaceholder')}
                  value={bookingModal.notes}
                  onChange={(e) => setBookingModal((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setBookingModal({
                    open: false,
                    slot: null,
                    contactId: '',
                    newContactName: '',
                    newContactPhone: '',
                    newContactEmail: '',
                    title: '',
                    notes: '',
                    type: 'call',
                  })}
                  className="rounded border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('crm.appointments.modal.cancel')}
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded bg-cyan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600 transition-colors shadow-md"
                >
                  <Clock className="h-4 w-4" /> {t('crm.appointments.modal.confirmBooking')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
